import React, { useState } from 'react';
import { NavLink as RouterLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Collapse, ListItem, Box, Tooltip } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const NavItem = ({
  children,
  className,
  depth,
  icon: Icon,
  info: Info,
  open: openProp = false,
  title,
  expanded,
  route,
  status,
  disabledState,
  ...rest
}) => {
  const location = useLocation();
  const [open, setOpen] = useState(openProp);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let marginLeft = 6;

  if (depth > 0) {
    marginLeft = 32 + 8 * depth;
  }

  const style = { marginLeft };

  const effectiveStatus = status || disabledState;
  const statusType = effectiveStatus?.type;
  const showError = statusType === 'error' || statusType === 'full';
  const showWarning = statusType === 'warning' || statusType === 'partial';
  const statusTooltip = effectiveStatus?.tooltip || '';

  if (children) {
    return (
      <ListItem
        sx={{
          display: 'block',
          paddingTop: 0,
          paddingBottom: 0,
          marginTop: '10px',
          marginBottom: '3px',
        }}
        className={className}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          sx={{
            color: 'text.primary',
            justifyContent: 'flex-start',
            textTransform: 'none',
            letterSpacing: 0,
            width: '100%',
            '&:hover': {
              backgroundColor: 'action.hover',
              borderRadius: '10px',
            },
          }}
          onClick={handleToggle}
          style={style}
        >
          {Icon && (
            <Box
              component={Icon}
              sx={{
                display: 'flex',
                flexShrink: 0,
                alignItems: 'center',
                marginRight: 2,
              }}
              size="20"
            />
          )}
          <Box sx={{ marginRight: 'auto' }}>{title}</Box>
          {/* Reserved status icon area */}
          <Box sx={{ width: 18, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {statusType && (showError || showWarning) && (
              <Tooltip title={statusTooltip}>
                <Box sx={{ lineHeight: 0 }}>
                  {showError ? (
                    <ErrorOutlineIcon fontSize="inherit" sx={{ color: 'error.main' }} />
                  ) : (
                    <WarningAmberOutlinedIcon fontSize="inherit" sx={{ color: 'warning.main' }} />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  const isContentRoute = route?.splitTarget === 'content';
  const targetHref = isContentRoute ? route?.externalUrl || route?.path : route?.path;
  const isActive =
    !isContentRoute && targetHref ? location.pathname === targetHref || location.pathname.startsWith(`${targetHref}/`) : false;

  const blocked = showError && effectiveStatus?.blocked;

  return (
    <ListItem
      sx={{
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0,
        marginTop: '10px',
        marginBottom: '3px',
      }}
      className={className}
      disableGutters
      key={title}
      {...rest}
    >
      {isContentRoute ? (
        <Button
          component="a"
          href={targetHref || '#'}
          style={style}
          sx={[
            {
              color: 'text.primary',
              width: '100%',
              fontWeight: 'fontWeightRegular',
              justifyContent: 'flex-start',
              textTransform: 'none',
              letterSpacing: 0,
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '10px',
              },
            },
            depth === 0 && {
              '& .nav-title': {
                fontWeight: 'fontWeightMedium',
              },
            },
          ]}
        >
          {Icon && (
            <Box
              component={Icon}
              className="nav-icon"
              sx={{
                display: 'flex',
                flexShrink: 0,
                alignItems: 'center',
                marginRight: 2,
              }}
              size="20"
            />
          )}
          <Box className="nav-title" sx={{ marginRight: 'auto' }}>
            {title}
          </Box>
          {/* Reserved status icon area */}
          <Box sx={{ width: 18, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {statusType && (showError || showWarning) && (
              <Tooltip title={statusTooltip}>
                <Box sx={{ lineHeight: 0 }}>
                  {showError ? (
                    <ErrorOutlineIcon fontSize="inherit" sx={{ color: 'error.main' }} />
                  ) : (
                    <WarningAmberOutlinedIcon fontSize="inherit" sx={{ color: 'warning.main' }} />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
          {Info && <Info />}
        </Button>
      ) : blocked ? (
        <Button
          style={style}
          sx={[
            {
              color: 'text.primary',
              width: '100%',
              fontWeight: 'fontWeightRegular',
              justifyContent: 'flex-start',
              textTransform: 'none',
              letterSpacing: 0,
              opacity: 0.6,
              cursor: 'not-allowed',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: '10px',
              },
            },
            depth === 0 && {
              '& .nav-title': {
                fontWeight: 'fontWeightMedium',
              },
            },
          ]}
        >
          {Icon && (
            <Box
              component={Icon}
              className="nav-icon"
              sx={{
                display: 'flex',
                flexShrink: 0,
                alignItems: 'center',
                marginRight: 2,
              }}
              size="20"
            />
          )}
          <Box className="nav-title" sx={{ marginRight: 'auto' }}>
            {title}
          </Box>
          <Box sx={{ width: 18, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {statusType && (showError || showWarning) && (
              <Tooltip title={statusTooltip}>
                <Box sx={{ lineHeight: 0 }}>
                  {showError ? (
                    <ErrorOutlineIcon fontSize="inherit" sx={{ color: 'error.main' }} />
                  ) : (
                    <WarningAmberOutlinedIcon fontSize="inherit" sx={{ color: 'warning.main' }} />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>
          {Info && <Info />}
        </Button>
      ) : (
        <RouterLink to={targetHref || '#'} style={{ textDecoration: 'none', width: '100%' }}>
          <Button
            style={style}
            sx={[
              {
                color: 'text.primary',
                width: '100%',
                fontWeight: 'fontWeightRegular',
                justifyContent: 'flex-start',
                textTransform: 'none',
                letterSpacing: 0,
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: '10px',
                },
              },
              depth === 0 && {
                '& .nav-title': {
                  fontWeight: 'fontWeightMedium',
                },
              },
              isActive && {
                color: 'primary.main',
                backgroundColor: 'action.selected',
                borderRadius: '10px',
                '& .nav-title': {
                  fontWeight: 'fontWeightMedium',
                },
                '& .nav-icon': {
                  color: 'primary.main',
                },
              },
            ]}
          >
            {Icon && (
              <Box
                component={Icon}
                className="nav-icon"
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  alignItems: 'center',
                  marginRight: 2,
                }}
                size="20"
              />
            )}
            <Box className="nav-title" sx={{ marginRight: 'auto' }}>
              {title}
            </Box>
            {/* Reserved status icon area */}
            <Box sx={{ width: 18, mr: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {statusType && (showError || showWarning) && (
                <Tooltip title={statusTooltip}>
                  <Box sx={{ lineHeight: 0 }}>
                    {showError ? (
                      <ErrorOutlineIcon fontSize="inherit" sx={{ color: 'error.main' }} />
                    ) : (
                      <WarningAmberOutlinedIcon fontSize="inherit" sx={{ color: 'warning.main' }} />
                    )}
                  </Box>
                </Tooltip>
              )}
            </Box>
            {Info && <Info />}
          </Button>
        </RouterLink>
      )}
    </ListItem>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  route: PropTypes.shape({
    accessible: PropTypes.bool,
    externalUrl: PropTypes.string,
    path: PropTypes.string,
    splitTarget: PropTypes.string,
  }),
  status: PropTypes.shape({
    type: PropTypes.oneOf(['warning', 'error', 'partial', 'full']),
    tooltip: PropTypes.string,
    blocked: PropTypes.bool,
  }),
  disabledState: PropTypes.shape({
    type: PropTypes.oneOf(['partial', 'full']),
    tooltip: PropTypes.string,
    blocked: PropTypes.bool,
  }),
};

export default NavItem;
